import React from 'react';
import { objestados, SubirArchivo, deshabilitar } from '../index.js';
import { Button } from '@material-tailwind/react';

// Íconos utilizados para indicar visualmente si se modifica o adjunta un archivo.
import { PencilIcon, PaperClipIcon, TrashIcon } from '@heroicons/react/24/outline';

import { Dialogos } from '../../../../../componentes/MUI/Dialogos.js';

/*
    PanelAdjuntosDinamico

    Componente reutilizable para visualizar, seleccionar o reemplazar un archivo adjunto.

    IMPORTANTE:
    Este componente NO sube directamente el archivo a DigitalOcean Spaces.

    Su responsabilidad es:
    1. Mostrar el archivo actualmente asociado, si existe una key en "url".
    2. Permitir abrir ese archivo mediante traerUrl(url).
    3. Permitir al usuario seleccionar un nuevo archivo utilizando SubirArchivo.
    4. Guardar temporalmente el objeto File seleccionado en el estado "nuevoArchivo"
       mediante setNuevoArchivo().

    La subida efectiva a DigitalOcean Spaces debe realizarse posteriormente desde
    la lógica de guardado del componente padre, por ejemplo mediante:

        subirAdjunto(nuevoArchivo, identificador, tipoArchivo, archivosSubidos)

    Flujo general:

        PanelAdjuntosDinamico
                ↓
        usuario selecciona archivo
                ↓
        SubirArchivo
                ↓
        setNuevoArchivo(File)
                ↓
        nuevoArchivo queda en memoria
                ↓
        componente padre ejecuta Guardar
                ↓
        subirAdjunto(...)
                ↓
        DigitalOcean Spaces
*/

export const PanelAdjuntosDinamico = ({
    // Texto descriptivo que se muestra en pantalla.
    // Ejemplo: "Analítico", "Adjunto", "Fichero de culto".
    titulo = "Archivo",

    // Key del archivo actualmente almacenado en DigitalOcean Spaces.
    // No necesariamente es una URL real aunque históricamente la prop se llame "url".
    // traerUrl(url) utilizará esta key para obtener una URL temporal de lectura.
    url,

    // Indica si el panel está actualmente en modo edición.
    editando,

    // Objeto File seleccionado por el usuario.
    // Mientras no se ejecute la lógica de guardado del padre, este archivo
    // solamente existe en memoria del navegador.
    nuevoArchivo,

    // Cambia el panel entre modo visualización y modo edición.
    setEditando,

    // Guarda o limpia el objeto File seleccionado.
    setNuevoArchivo,

    // Función utilizada para obtener y abrir la URL temporal de un archivo
    // previamente almacenado en DigitalOcean Spaces.
    traerUrl,

    // Si es true, el usuario puede visualizar el archivo pero no modificarlo.
    deshabilitado,

    // Si es true, elimina las clases visuales externas del panel para permitir
    // integrarlo dentro de otros layouts.
    removeClasses,

    // Si es true, el botón de adjuntar/modificar se muestra como ícono.
    // Si es false, se muestra como texto.
    icono = false

}) => {

    /*
        Estado auxiliar actualmente no utilizado.

        Si no existe alguna lógica pendiente que dependa de este estado,
        podría eliminarse junto con setAux.
    */
    const [aux, setAux] = React.useState(false);

    // Clases utilizadas en el modo visual estándar del componente.
    const class1 =
        "bg-slate-50 p-3 rounded-lg border border-slate-100/70 flex flex-col justify-between gap-2 min-h-[68px] w-full";

    const class2 =
        "flex items-center justify-between w-full text-sm";

    const class3 =
        "cursor-pointer text-blue-500 font-bold hover:underline flex items-center gap-1.5";

    return (

        /*
            removeClasses permite utilizar el componente con o sin su contenedor visual
            predeterminado.
        */
        <div className={removeClasses ? '' : class1}>

            {!editando ? (

                /*
                    ============================================================
                    MODO VISUALIZACIÓN
                    ============================================================

                    Mientras editando === false:

                    - Si existe "url", significa que ya existe un archivo asociado.
                    - Si no existe "url", se informa que todavía no hay archivo.
                    - Si el usuario tiene permisos, puede pasar al modo edición.
                */

                <div className={removeClasses ? '' : class2}>

                    {url ? (

                        /*
                            ARCHIVO EXISTENTE

                            "url" contiene en realidad la key almacenada en Spaces.

                            Al hacer click se ejecuta traerUrl(url), que será responsable
                            de pedir al backend una URL temporal/presigned URL y abrir
                            el archivo.
                        */
                        <span
                            title={`Visualice el ${titulo.toLowerCase()} adjunto`}
                            className={
                                removeClasses
                                    ? 'cursor-pointer text-blue-500'
                                    : class3
                            }
                            onClick={() => traerUrl(url)}
                        >
                            📄 {titulo}
                        </span>

                    ) : (

                        /*
                            No existe actualmente un archivo asociado.
                        */
                        <span className="text-slate-400 text-xs italic">
                            Sin {titulo.toLowerCase()}
                        </span>
                    )}

                    {/*
                        Si el panel no está deshabilitado, se permite adjuntar
                        o reemplazar el archivo.
                    */}
                    {!deshabilitado && (

                        <Button
                            size="sm"
                            variant="text"
                            color="blue-gray"

                            className={
                                `py-1 px-2 font-bold ${
                                    icono
                                        ? 'p-1.5 rounded-full'
                                        : 'text-xs'
                                }`
                            }

                            title={
                                url
                                    ? 'Modificar archivo'
                                    : 'Adjuntar archivo'
                            }

                            /*
                                Al presionar el botón solamente cambiamos a modo edición.

                                Todavía NO se sube ningún archivo a Spaces.
                            */
                            onClick={() => setEditando(true)}
                        >

                            {/*
                                Cuando icono=true:

                                - Si ya existe un archivo se muestra lápiz.
                                - Si no existe se muestra clip.

                                Cuando icono=false:

                                - Se muestra "Modificar" o "Adjuntar".
                            */}
                            {icono ? (

                                url ? (

                                    <PencilIcon className="w-4 h-4 text-slate-600" />

                                ) : (

                                    <PaperClipIcon className="w-4 h-4 text-slate-600" />
                                )

                            ) : (

                                url
                                    ? 'Modificar'
                                    : 'Adjuntar'
                            )}

                        </Button>
                    )}

                </div>

            ) : (

                /*
                    ============================================================
                    MODO EDICIÓN
                    ============================================================

                    Cuando editando === true se muestra SubirArchivo.

                    SubirArchivo permite seleccionar el PDF y termina ejecutando:

                        setNuevoArchivo(file)

                    De esa manera el objeto File queda almacenado temporalmente
                    en "nuevoArchivo".

                    IMPORTANTE:
                    seleccionar el archivo NO significa que ya se haya subido a Spaces.
                */

                <div className="w-full flex items-center justify-between gap-2">

                    <SubirArchivo
                        texto=""
                        titulo={`Nuevo ${titulo.toLowerCase()}`}

                        // File seleccionado actualmente.
                        file={nuevoArchivo}

                        // En este componente solamente permitimos PDF.
                        tiposFiles={['.pdf']}

                        // Tamaño máximo: 5 MB.
                        maximo={5000000}

                        /*
                            Esta función es la conexión entre SubirArchivo y el estado
                            del componente padre.

                            Por ejemplo, si el padre pasó:

                                setNuevoArchivo={setNuevoAdjunto}

                            cuando el usuario seleccione CAT ONIX.pdf ocurrirá:

                                setNuevoAdjunto(File)
                        */
                        setFile={setNuevoArchivo}

                        /*
                            Ocultamos el botón interno de eliminación porque este panel
                            ya dispone de su propio botón externo.
                        */
                        noDelete={true}
                    />

                    {/*
                        Cancela la selección/modificación.

                        Limpia el File de memoria y vuelve al modo visualización.

                        No elimina ningún archivo de Spaces porque en este punto
                        nuevoArchivo todavía no fue subido.
                    */}
                    <button
                        type="button"
                        title="Cancelar modificación del archivo"

                        onClick={() => {
                            setNuevoArchivo(null);
                            setEditando(false);
                        }}

                        className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded transition-colors"
                    >
                        <TrashIcon width={20} height={20} />
                    </button>

                </div>
            )}

            {/*
                ================================================================
                DIÁLOGO DE SELECCIÓN
                ================================================================

                El diálogo se abre cuando:

                    editando === true
                    &&
                    nuevoArchivo === null

                Es decir, el usuario inició la modificación pero todavía no seleccionó
                un nuevo archivo.

                Cuando selecciona el archivo, setNuevoArchivo() deja de ser null
                y esta condición deja de cumplirse.
            */}
            <Dialogos
                open={editando && !nuevoArchivo}
                procesarCancelar={() => setEditando(false)}
            >

                <div className="w-full flex items-center justify-between gap-2">

                    <SubirArchivo
                        texto=""
                        titulo={`Nuevo ${titulo.toLowerCase()}`}
                        file={nuevoArchivo}
                        tiposFiles={['.pdf']}
                        maximo={5000000}

                        /*
                            Nuevamente, el File seleccionado se guarda en el estado
                            proporcionado por el componente padre.
                        */
                        setFile={setNuevoArchivo}
                    />

                    {/*
                        Cancela la selección desde el diálogo.
                    */}
                    <button
                        type="button"
                        className="text-xs font-bold text-red-500 hover:underline p-1 px-2"

                        onClick={() => {
                            setNuevoArchivo(null);
                            setEditando(false);
                        }}
                    >
                        X
                    </button>

                </div>

            </Dialogos>

        </div>
    );
};